import { FunctionComponent, useState, useEffect } from 'react'
import { Container, Row, Button, Col } from 'react-bootstrap'
import { useIDToken } from '../state/modules/auth'
import { SolarLeaderboardSiteSource } from '@black-bear-energy/black-bear-energy-common'
import { SolarLeaderboardUploader } from '../components/SolarLeaderboardUpload/SolarLeaderboardUploader'
import { REITSelect } from '../components/SolarLeaderboardUpload/REITSelect'
import { useNotifications } from '../state/modules/notifications'
import { useCallOnceOnIDTokenGranted } from '../state/modules/common'
import { Variant, RequestStatus } from '../common/types'
import { useREITs, useLoadREITs, REIT } from '../state/modules/reits'
import { UpsertCounts } from '../components/helpers/DromoUploader/chunkSiteListUpload'
import { getAPI } from '../utils/api'
import { legacyLayoutProps } from './constants'

export type upsertCounts = { newCount: number; updateCount: number }

export const SolarLeaderboardUpload: FunctionComponent = () => {
  const [reit, setREIT] = useState<REIT | undefined>(undefined)
  const [showModal, setShowModal] = useState(false)
  const [uploadInProgress, setUploadInProgress] = useState(false)
  const [source, setSource] =
    useState<keyof typeof SolarLeaderboardSiteSource>('Internal')
  const [sourceOther, setSourceOther] = useState<string | undefined>(undefined)
  const [reloadREITs, setReloadREITs] = useState(false)
  const accessToken = useIDToken()
  const { send: sendNotification } = useNotifications()

  const {
    payload: reits,
    status: reitsStatus,
    httpErrors: reitsHTTPErrors,
  } = useREITs()
  const loadREITs = useLoadREITs()

  /**
   * Hook to load list of REITs.
   *
   * Note - useCallOnceOnIDTokenGranted, so that it only calls the api when the idToken is fetched!
   */
  useCallOnceOnIDTokenGranted(async () => {
    if (reitsStatus === RequestStatus.IDLE) {
      await loadREITs()
    }
  })

  useEffect(() => {
    if (!reloadREITs) {
      return
    }
    void loadREITs()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadREITs])

  function handleREITSelect(reit?: REIT) {
    setREIT(reit)
    setReloadREITs(false)
  }

  async function handleUploadClick() {
    setUploadInProgress(true)
    // if the user has manually entered a new REIT name, create that record first
    if (reit?.id === -1) {
      await createNewREIT()
    }
    setShowModal(true)
  }

  function handleUploadStart() {
    sendSolarLeaderBoardUploadNotification(
      'Uploading site list...',
      Variant.INFO
    )
  }

  function exitModal(counts?: UpsertCounts) {
    setUploadInProgress(false)
    setShowModal(false)
    if (counts) {
      sendSolarLeaderBoardUploadNotification(
        getUploadSuccessNotification(counts),
        Variant.SUCCESS
      )
    }
  }

  function getUploadSuccessNotification(counts: UpsertCounts): string {
    // handle a successful upload with no updates or new sites
    if (counts.newCount === 0 && counts.updateCount === 0) {
      return 'Upload completed successfully. No new sites added and no changes made to existing sites.'
    }

    const newCountString = counts.newCount
      ? `imported ${counts.newCount} new site${counts.newCount > 1 ? 's' : ''}`
      : ''
    const updateCountString = counts.updateCount
      ? `updated ${counts.updateCount} existing site${
          counts.updateCount > 1 ? 's' : ''
        }`
      : ''

    return `Successfully ${newCountString}
              ${newCountString && updateCountString ? ' and ' : ''}
              ${updateCountString}`
  }

  async function createNewREIT() {
    const api = getAPI(accessToken)
    try {
      const newREITResponse = await api.post<REIT>('/reits', {
        name: reit?.name,
      })
      setREIT(newREITResponse.data)
      setReloadREITs(true)
    } catch (e) {
      handleNewREITError()
    }
  }

  function handleNewREITError() {
    sendSolarLeaderBoardUploadNotification(
      'An error occurred while creating a new REIT',
      Variant.DANGER
    )
    setUploadInProgress(false)
  }

  function handleImportError() {
    setUploadInProgress(false)
    setShowModal(false)
    sendSolarLeaderBoardUploadNotification(
      'An error occurred while uploading the site list',
      Variant.DANGER
    )
  }

  /**
   * Display a toast notification using the site RFP name as the title.
   * @param message - The notification message.
   * @param variant - The variant, which determines the color of the notification.
   */
  const sendSolarLeaderBoardUploadNotification = (
    message: string,
    variant: Variant
  ) => {
    sendNotification({
      title: 'Solar Leaderboard Upload',
      message,
      variant,
    })
  }

  const sourceRadioOptions = (
    Object.keys(SolarLeaderboardSiteSource) as Array<
      keyof typeof SolarLeaderboardSiteSource
    >
  ).map((key) => (
    <Col key={key}>
      <input
        type="radio"
        id={key}
        value={SolarLeaderboardSiteSource[key]}
        checked={source === key}
        onChange={() => {
          setSourceOther(undefined)
          setSource(key)
        }}
        style={{ marginRight: '5px' }}
      />
      <label htmlFor={key}>{SolarLeaderboardSiteSource[key]}</label>
    </Col>
  ))

  const disableUploadBtn =
    !reit ||
    uploadInProgress ||
    (source === SolarLeaderboardSiteSource.Other && sourceOther === null)

  return (
    <Container {...legacyLayoutProps}>
      <Row className="my-4">
        <span className="mt-1">
          Upload and de-duplicate a list of REIT sites for the Solar
          Leaderboard.
        </span>
      </Row>
      <Row className="my-4">
        <REITSelect
          selectedREIT={reit}
          reitList={reits}
          onREITChange={handleREITSelect}
          disabled={false}
          allowNewValue={true}
          reitsStatus={reitsStatus}
          reitsHTTPErrors={reitsHTTPErrors}
        />
      </Row>
      <Row className="my-4" style={{ maxWidth: '700px' }}>
        <span className="mt-1">The source of this site list.</span>
      </Row>
      <Row className="my-4">
        <div style={{ display: 'flex', width: '800px' }}>
          {sourceRadioOptions}
        </div>
        {source === SolarLeaderboardSiteSource.Other && (
          <Col style={{ marginLeft: '-75px' }}>
            <input
              type="text"
              placeholder="Enter source"
              onKeyUp={(event) => {
                const sourceOther = event.currentTarget.value
                setSourceOther(sourceOther == '' ? undefined : sourceOther)
              }}
            />
          </Col>
        )}
      </Row>
      <Row className="my-4">
        <Button
          className={'btn btn-primary'}
          disabled={disableUploadBtn}
          onClick={() => {
            void handleUploadClick()
          }}
        >
          Upload Sites
        </Button>
        <SolarLeaderboardUploader
          reitId={reit?.id}
          reitList={reits}
          source={source}
          sourceOther={sourceOther}
          isOpen={showModal}
          onUploadStart={() => handleUploadStart()}
          onUploadError={() => handleImportError()}
          onClose={(counts?: UpsertCounts) => exitModal(counts)}
        />
      </Row>
    </Container>
  )
}
